// AppService paginates some /public/v1 collections by cursor: follow
// X-Pagination-Cursor-Next until it is absent, sending it back as
// X-Pagination-Cursor. The body stays a plain array, so callers keep receiving
// the whole collection and do not have to know about paging.
//
// It is a keyset cursor: an item created or deleted between two pages neither
// shifts nor duplicates the rest.
//
// We deliberately do not send X-Pagination-Size. The server already defaults to
// 100 and caps there, so it would buy nothing — and it would add a custom header
// to the very first request, whose preflight works today. Only page 2 onwards
// carries a new header, and we only ever get there if the cursor was readable.

// Total across all pages, present on every paginated response.
const HEADER_ELEMENTS = 'x-pagination-elements'
const HEADER_CURSOR_NEXT = 'x-pagination-cursor-next'

// Only a runaway server (a cursor that never clears) can reach this. Kept high
// enough that no real project hits it, low enough to fail rather than hang.
const MAX_PAGES = 200

/**
 * Read one page and pull the paging information out of its headers.
 *
 * @returns {Promise<{ body: *, next: string|null, total: number|null }>}
 */
async function readPage (client, { url, params, cursor }) {
  const headers = cursor ? { 'X-Pagination-Cursor': cursor } : {}
  const response = await client.request({
    method: 'get',
    url,
    params,
    headers,
    originalResponse: true
  })

  const reported = Number(response?.headers?.[HEADER_ELEMENTS])
  return {
    body: response?.data,
    // Its absence, not an empty page, is what ends the collection.
    next: response?.headers?.[HEADER_CURSOR_NEXT] || null,
    total: Number.isFinite(reported) ? reported : null
  }
}

/**
 * A single response that announces more items than it carried means the cursor
 * never reached us — typically because the browser hid the header for want of
 * Access-Control-Expose-Headers. Without this the collection would be silently
 * capped at one page.
 */
function assertNothingHidden ({ url, pages, total, count }) {
  if (pages !== 1 || total === null || total <= count) return
  throw new Error(`${url}: ${total} items announced but only ${count} readable — is X-Pagination-Cursor-Next exposed?`)
}

/**
 * Read every page of a cursor-paginated collection.
 *
 * Works against a server that does not paginate yet: without the
 * X-Pagination-Cursor-Next header the loop stops after the first response.
 *
 * Throws rather than returning a partial list, so the caller reports it instead
 * of quietly showing a truncated collection.
 *
 * @param {object} client an API instance (anything with `request`)
 * @param {{ url: string, params?: object }} options
 * @returns {Promise<Array|*>} the full collection, or the raw body when the
 *   server answered something that is not an array on the very first call
 */
export async function fetchAllPages (client, { url, params } = {}) {
  const first = await readPage(client, { url, params, cursor: null })

  // A server that has not moved to cursor pagination still answers with the
  // full collection; hand it back rather than dropping it.
  if (!Array.isArray(first.body)) return first.body

  const items = [...first.body]
  let { next, total } = first
  let cursor = null
  let pages = 1

  while (next) {
    if (next === cursor) throw new Error(`${url}: the cursor repeated, refusing to loop`)
    if (pages >= MAX_PAGES) throw new Error(`${url}: stopped after ${MAX_PAGES} pages, the cursor never cleared`)
    cursor = next

    const page = await readPage(client, { url, params, cursor })
    // Mid-walk this is an error, and keeping the pages already read would pass
    // a truncated list off as complete.
    if (!Array.isArray(page.body)) throw new Error(`${url}: page ${pages + 1} is not an array`)

    items.push(...page.body)
    pages += 1
    if (page.total !== null) total = page.total
    next = page.next
  }

  assertNothingHidden({ url, pages, total, count: items.length })
  return items
}
